import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import {
  ClearEjournalSession,
  ResetEjournalSession,
  SetEjournal,
  SetEjournalError,
  SetEjournalLoading,
  SetEntries,
  SetEntriesError,
  SetEntriesLoading,
  SetIp,
  SetJournalPayload,
  SetNotarizedAuditActions,
  SetNotarizedAuditLoaded,
  SetPageNamesByNumber,
  SetProfile,
  SetSelectedPacket,
  SetSigners,
  SetSignersError,
  SetSignersLoading,
  UpdateEntries,
  UpdateSigners,
} from './ejournal-session.actions';
import {
  EJOURNAL_SESSION_DEFAULTS,
  EjournalSessionModel,
} from './ejournal-session.model';

@State<EjournalSessionModel>({
  name: 'EjournalSessionStore',
  defaults: EJOURNAL_SESSION_DEFAULTS,
})
@Injectable()
export class EjournalSessionState {
  @Action(SetProfile)
  setProfile(ctx: StateContext<EjournalSessionModel>, { profile }: SetProfile) {
    ctx.patchState({ profile });
  }

  @Action(SetSelectedPacket)
  setSelectedPacket(
    ctx: StateContext<EjournalSessionModel>,
    { selectedPacket }: SetSelectedPacket,
  ) {
    ctx.patchState({ selectedPacket });
  }

  @Action(SetEjournal)
  setEjournal(
    ctx: StateContext<EjournalSessionModel>,
    { eJournal }: SetEjournal,
  ) {
    ctx.patchState({ eJournal });
  }

  @Action(SetSigners)
  setSigners(ctx: StateContext<EjournalSessionModel>, { signers }: SetSigners) {
    ctx.patchState({ signers });
  }

  @Action(SetEntries)
  setEntries(ctx: StateContext<EjournalSessionModel>, { entries }: SetEntries) {
    ctx.patchState({ entries });
  }

  @Action(SetNotarizedAuditActions)
  setNotarizedAuditActions(
    ctx: StateContext<EjournalSessionModel>,
    { notarizedAuditActions }: SetNotarizedAuditActions,
  ) {
    ctx.patchState({ notarizedAuditActions });
  }

  @Action(SetNotarizedAuditLoaded)
  setNotarizedAuditLoaded(
    ctx: StateContext<EjournalSessionModel>,
    { notarizedAuditLoaded }: SetNotarizedAuditLoaded,
  ) {
    ctx.patchState({ notarizedAuditLoaded });
  }

  @Action(SetPageNamesByNumber)
  setPageNamesByNumber(
    ctx: StateContext<EjournalSessionModel>,
    { pageNamesByNumber }: SetPageNamesByNumber,
  ) {
    ctx.patchState({ pageNamesByNumber });
  }

  @Action(UpdateSigners)
  updateSigners(
    ctx: StateContext<EjournalSessionModel>,
    { updater }: UpdateSigners,
  ) {
    ctx.setState(
      patch<EjournalSessionModel>({
        signers: updater(ctx.getState().signers),
      }),
    );
  }

  @Action(UpdateEntries)
  updateEntries(
    ctx: StateContext<EjournalSessionModel>,
    { updater }: UpdateEntries,
  ) {
    ctx.setState(
      patch<EjournalSessionModel>({
        entries: updater(ctx.getState().entries),
      }),
    );
  }

  @Action(SetEjournalLoading)
  setEjournalLoading(
    ctx: StateContext<EjournalSessionModel>,
    { eJournalLoading }: SetEjournalLoading,
  ) {
    ctx.patchState({ eJournalLoading });
  }

  @Action(SetSignersLoading)
  setSignersLoading(
    ctx: StateContext<EjournalSessionModel>,
    { signersLoading }: SetSignersLoading,
  ) {
    ctx.patchState({ signersLoading });
  }

  @Action(SetEntriesLoading)
  setEntriesLoading(
    ctx: StateContext<EjournalSessionModel>,
    { entriesLoading }: SetEntriesLoading,
  ) {
    ctx.patchState({ entriesLoading });
  }

  @Action(SetEjournalError)
  setEjournalError(
    ctx: StateContext<EjournalSessionModel>,
    { eJournalError }: SetEjournalError,
  ) {
    ctx.patchState({ eJournalError });
  }

  @Action(SetSignersError)
  setSignersError(
    ctx: StateContext<EjournalSessionModel>,
    { signersError }: SetSignersError,
  ) {
    ctx.patchState({ signersError });
  }

  @Action(SetEntriesError)
  setEntriesError(
    ctx: StateContext<EjournalSessionModel>,
    { entriesError }: SetEntriesError,
  ) {
    ctx.patchState({ entriesError });
  }

  @Action(SetIp)
  setIp(ctx: StateContext<EjournalSessionModel>, { ip }: SetIp) {
    ctx.patchState({ ip });
  }

  @Action(SetJournalPayload)
  setJournalPayload(
    ctx: StateContext<EjournalSessionModel>,
    { eJournal, signers, entries }: SetJournalPayload,
  ) {
    ctx.patchState({ eJournal, signers, entries });
  }

  @Action(ResetEjournalSession)
  resetEjournalSession(ctx: StateContext<EjournalSessionModel>) {
    ctx.patchState({
      eJournal: null,
      signers: [],
      entries: [],
      notarizedAuditActions: [],
      notarizedAuditLoaded: false,
      pageNamesByNumber: {},
      eJournalError: null,
      signersError: null,
      entriesError: null,
    });
  }

  @Action(ClearEjournalSession)
  clearEjournalSession(ctx: StateContext<EjournalSessionModel>) {
    ctx.setState(EJOURNAL_SESSION_DEFAULTS);
  }
}
